/**
 * watchlist.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "app/auth/identity/user.service";
import { Rule } from "app/auth/permissions/rules";
import { SortTarget } from "app/common/data/data-sorting-header.component";
import { ModalService } from "app/common/modal.service";
import { SettingService } from "app/configuration/setting.service";
import { WorklistConfigModalComponent } from "app/server/messages/watchlists/worklist-config-modal.component";
import { hardEquals, oneline } from "app/support/utilities";
import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { Algorithm } from "../algorithms/structure/algorithm";
import { Subject } from "../subjects/subject";
import { SubjectContextType } from "../subjects/subject-context-type";
import { WatchlistEntry, WatchlistTable } from "./watchlist-entry";
import { WatchlistComponent } from "./watchlist.component";
import { SharedList, WatchlistService } from "./watchlist.service";
import { SubjectService } from "app/subjects/subject.service";
import { AlertService } from "app/common/alert.service";
import { AcknowledgmentResponse } from "app/server/messages/general";
import { CreateSharedListModalComponent, SharedListModalResult } from "./create-shared-list-modal.component";

@Component({
  selector: "shared-worklist",
  template: `
  <div class="container" *ngIf="list !== null">
    <div class="row">
      <div class="col-sm-2">
        <button class="btn btn-primary" routerLink="/lists">
          ← Back to List Browser
        </button>
      </div>
      <div class="col-sm-8 jumbotron">
        This is a shared list with visibility <span class="badge">group-public</span>, meaning anyone else in your security group (<u>ABC for Health</u>) with permission to see shared lists can access it. Note that, unlike your personal worklist, this:
        <ul>
          <li>does not update automatically while on the page</li>
          <li>does not yet support notifications for entry changes</li>
          <li>may have issues with sorting and folders</li>
        </ul>
        Please report any issues and suggestions to MCP.
      </div>
    </div>
    <h2 *ngIf="showTitle">
      <span class="text-muted">List:</span>
      {{list.title}}
      <span class="badge">{{list.worklist.length}}</span>
      <div class="btn btn-sm btn-default pull-right" (click)="editList()" [class.disabled]="!canEdit()">
        Edit Details
      </div>
    </h2>
    <p *ngIf="list.description">
      {{list.description}}
      <em *ngIf="!list.description">None provided.</em>
    </p>
    <div *ngFor="let tab of entryTables">
    <table
      *ngIf="!enforcedType || tab.contextType === enforcedType"
      class="table table-responsive">
      <caption
        *ngIf="entryTables.length > 1"
        class="type capitalized">
        {{tab.contextType}}
      </caption>
      <thead>
        <tr>
          <th
            *ngFor="let column of tab.columns; let i = index"
            [sorts]="tab.entries"
            [dot]="'summary.' + i"
            storedAs="worklistSorting"
            [class.capitalized]="true"
            [class.hidden]="ignoredColumns.has(column)">
            {{column}}
          </th>
          <!-- <th
            [sorts]="tab.entries"
            dot="lastObserved"
            storedAs="worklistSorting"
            asType="integer">
            Last Viewed
          </th> -->
          <th
            [class.hidden]="notesHidden()"
            [sorts]="tab.entries"
            asType="string"
            dot="note"
            storedAs="worklistSorting">
            Notes
            <div class="subtitle">(shared by all users)</div>
          </th>
          <!-- <th [class.hidden]="notificationsHidden()">
            Notify via
            <div class="subtitle">click to change</div>
          </th> -->
          <th colspan="1" class="text-center">
            Actions
            <!-- <button
              class="btn btn-sm btn-default"
              (click)="openConfigModal()">
              Configure Columns...
            </button> -->
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          ngFor
          let-entry
          [ngForOf]="(tab.entries | paginate)">
          <!-- (dragover)="allowDrag($event); folder.highlight = true"
          (dragenter)="allowDrag($event); folder.highlight = true"
          (dragleave)="folder.highlight = false"
          (drop)="dropItem($event, entry.categories[0], tab); folder.highlight = false" -->
        <tr
          #folder
          *ngIf="folderMode(tab) && firstOfFolder(entry, tab)"
          class="item-category"
          [class.drag-target]="folder.highlight"
          [class.droppable]="folder.highlight"
          [class.info]="entry.categories[0]"
          [class.warning]="!entry.categories[0]">
          <td>
            <span class="glyphicon glyphicon-folder-open"></span>
          </td>
          <td colspan="20">
            {{entry.categories[0] || 'Unsorted'}}
          </td>
        </tr>
        <tr [class.bg-warning]="entry.alert">
          <td
            *ngFor="let data of entry.summary"
            [class.hidden]="ignoredColumns.has(data.displayName)">
            <typed-display [input]="data"></typed-display>
          </td>
          <!-- <td
            [title]="entry.alert ? 'Changes since you last viewed' : ''">
            <div [class.emph]="entry.alert">
              {{entry.lastObserved | utcDate}}
            </div>
            <a
              class="btn btn-xs btn-warning"
              *ngIf="entry.alert"
              (click)="dismiss(entry)">
              mark as read
            </a>
          </td> -->
          <td #noteCell [class.hidden]="notesHidden()">
            <div>
              {{entry.note || "n/a"}}
              <a class="glyphicon glyphicon-pencil pull-right" (click)="showEditDialog(entry)"></a>
            </div>
          </td>
          <!-- <td
            (click)="changeNotificationSetting(entry)"
            [class.hidden]="notificationsHidden()">
            <span *ngIf="entry.emailNotifyOnUpdate">
              <span class="glyphicon glyphicon-envelope"></span>
              <code>email</code>
            </span>
            <span *ngIf="!entry.emailNotifyOnUpdate">
              <span class="glyphicon glyphicon-volume-off"></span>
              <code>none</code>
            </span>
          </td> -->
          <td class="text-center action-cell">
            <div role="group">
              <button
                *ngIf="entry.watchId === null"
                class="btn btn-sm btn-primary"
                title="You do not have permission to view this data"
                disabled>
                {{asSelector ? "Use" : "Open"}}
              </button>
              <a
                *ngIf="entry.watchId !== null && asSelector"
                class="btn btn-sm btn-primary"
				(click)="open(entry)">
                <span class="hidden-sm hidden-xs">Use</span>
                <span
                  class="hidden-md hidden-lg glyphicon glyphicon-folder-open">
                </span>
			        </a>
              <div
                #openOptions
                *ngIf="entry.watchId !== null && !asSelector"
                class="btn-group"
                [class.open]="openOptions.open"
                (mouseleave)="openOptions.open = false">
                <a
                  class="btn btn-sm btn-primary"
                  (click)="openDefault(entry)">
                  <span class="hidden-sm hidden-xs">Open</span>
                  <span
                  class="hidden-md hidden-lg glyphicon glyphicon-folder-open">
                  </span>
                </a>
                <button
                  type="button"
                  class="btn btn-sm btn-primary dropdown-toggle"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  [attr.aria-expanded]="!!openOptions.open"
                  (click)="openOptions.open = !openOptions.open">
                  <span class="caret"></span>
                </button>
                <ul class="dropdown-menu right-edge">
                  <li>
                    <a (click)="open(entry, null)">
                      Summary Page
                    </a>
                  </li>
                  <li role="separator" class="divider"></li>
                  <li *ngFor="let alg of algorithms.bySCType(tab.contextType)">
                    <a (click)="open(entry, alg)">
                      {{alg.displayName}}
                    </a>
                  </li>
                </ul>
              </div>
              <div
                #actionOptions
                *ngIf="entry.watchId !== null && !asSelector && canEdit()"
                class="btn-group"
                [class.open]="actionOptions.open"
                (mouseleave)="actionOptions.open = false">
                <a
                  class="btn btn-sm btn-default dropdown-toggle"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  [attr.aria-expanded]="!!actionOptions.open"
                  (click)="actionOptions.open = !actionOptions.open">
                  <span class="hidden-sm hidden-xs">Other...</span>
                  <span class="caret"></span>
                </a>
                <ul class="dropdown-menu right-edge">
                  <li [class.disabled]="!canEdit()">
                    <a (click)="unwatch(entry)">
                      Remove entry
                    </a>
                  </li>
                  <li class="disabled">
                    <a (click)="moveCopyPersonal(entry, true)">
                      Move to personal list
                    </a>
                  </li>
                  <li class="disabled">
                    <a (click)="moveCopyPersonal(entry, false)">
                      Copy to personal list
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </td>
        </tr>
        </template>
      </tbody>
      <caption>
        <pager [array]="tab.entries" [asNeeded]="true"></pager>
      </caption>
    </table>
    </div>
    <h4 class="text-center" *ngIf="entryTables.length === 0">
      This list is empty!
    </h4>
</div>
`,
  styles: [`
    caption {
      caption-side: bottom;
      font-size: 1.7em;
      font-style: italic;
      padding: 0.5em;
    }
    caption.empty {
      text-align: center;
    }
    caption.type {
      caption-side: top;
      margin: 0 20px;
      text-align: left;
    }
    .faded, .grabber {
      color: #DDDDDD;
    }
    table.table>tbody>tr>td {
      vertical-align: middle;
    }
    .form-control.focus-only {
      border: 1px transparent;
      box-shadow: none;
    }
    .form-control.focus-only:focus, .form-control.focus-only:hover  {
      border: 1px solid #ccc;
    }
    .hidden {
      display: none;
    }
    textarea {
      resize: none;
    }
    .focus-text {
      white-space: pre-line;
      border: 1px solid transparent;
      border-radius: 5px;
      padding: 5px;
      min-height: 34px;
      width: 100%;
    }
    .focus-text:hover {
      border: 1px solid #ccc;
    }
    th .subtitle {
      font-size: 80%;
      font-weight: normal;
    }
    .btn-group {
      overflow-y: none;
	}
	.action-cell {
		min-width: 96px;
  }
  .item-category {
    font-style: italic;
  }
  .item-category.unsorted {
    color: gray;
  }
  .droppable {
    cursor: copy;
  }
  .grabbable {
    cursor: grab;
  }
  .drag-target {
    font-weight: bold;
  }
  .badge.primary {
    background: #1D3559;
    border-color: lighten(#1D3559, 10%);
  }
  `]
})
export class SharedWorklistComponent implements OnInit
{
  @Input()
  public enforcedType : SubjectContextType;

  @Input()
  public showTitle : boolean = true;

  @Input()
  public asSelector : boolean = false;

  @Output()
  public selection : EventEmitter<Subject> = new EventEmitter();

  public ignoredColumns : Set<string> = this.getIgnored();

  private getIgnored () : Set<string>
  {
    const s : string = this.settings.get("ignored worklist columns") || "";
    if (s === "")
    {
      return new Set();
    }
    return new Set(s.split(","));
  }

  public openConfigModal () : void
  {
    alert("Config not available for shared lists");
    return;

    // const columns : string[] =
    //   [
    //     WatchlistComponent.NotesColumnLabel,
    //     WatchlistComponent.NotificationColumnLabel
    //   ].concat(this.watchlist.knownColumns);
    // this.modals.observe(
    //   WorklistConfigModalComponent,
    //   {
    //       columns,
    //       hidden: Array.from(this.ignoredColumns)
    //   }
    // ).subscribe((result : any) => {
    //   if (Array.isArray(result))
    //   {
    //     this.ignoredColumns = new Set(result);
    //     this.settings.set("ignored worklist columns", result.join(","));
    //   }
    //   else
    //   {
    //     console.warn("Got unexpected non-array from modal result:", result);
    //   }
    // });
  }

  public notesHidden () : boolean
  {
    return false;
    return this.ignoredColumns.has(WatchlistComponent.NotesColumnLabel);
  }
  public notificationsHidden () : boolean
  {
    return this.ignoredColumns.has(WatchlistComponent.NotificationColumnLabel);
  }
  public foldersHidden () : boolean
  {
    return this.ignoredColumns.has(WatchlistComponent.FolderColumnLabel);
  }

  public readonly FOLDER_SORT : string = "categories.0";
  public folderMode (table : WatchlistTable) : boolean
  {
    const sorting : SortTarget = table.entries;
    return sorting._sort && sorting._sort.dot === this.FOLDER_SORT;
  }

  public constructor (
    public watchlist : WatchlistService,
    public algorithms : AlgorithmService,
    public userSvc : UserService,
    public settings : SettingService,
    public subjects : SubjectService,
    public modals : ModalService,
    public router : Router,
    public route : ActivatedRoute,
    public alerts : AlertService
  ) {/* DI */}

  public ngOnInit () : void
  {
    this.route.data.subscribe((data : any) => {
      if (data.list)
      {
        this.list = data.list;
        this.groupId = +this.route.snapshot.params.groupId;
        this.entryTables = this.watchlist.sharedEntryTables.get(this.groupId);
      }
    });
  }

  public list : SharedList | null = null;
  public groupId : number | null = null;
  public entryTables : WatchlistTable[] = [];

  /**
   * Remove a currently-watched item from the watchlist
   * @param entry entry to be removed
   */
  public unwatch (entry : WatchlistEntry) : void
  {
    if (!this.canEdit()) return;
    this.watchlist.unwatch(entry, true, false, this.list.objectId).subscribe(
      (result : boolean) => {
        if (!result)
        {
          this.modals.confirm({
            title: "Unsubscribe failed",
            body: `The server failed to remove this record from the shared list.
                  It could be that this is because the record has been deleted
                  or migrated. Do you want to attempt to forcefully clean up
                  remaining subscription data?`,
            yesLabel: "Forcefully Remove",
            cancelLabel: "Nevermind",
            dismissValue: false
          }).subscribe((retry : boolean) => {
            if (retry)
            {
              this.watchlist.unwatch(entry, true, true, this.list.objectId).subscribe();
            }
          });
        }
      }
    );
  }

  public canEdit () : boolean
  {
    const group = this.userSvc.self.groupById(this.groupId);
    if (!group) {
      console.error(`Can't find group ID ${this.groupId}`);
      return false;
    }
    return group.rules.includes(Rule.CREATE_SHARED_WORKLIST);
  }

  public editList () : void
  {
    if (!this.canEdit()) {
      return alert(`You do not have permission to edit shared worklists!`);
    }
    this.modals.observe<SharedListModalResult>(
      CreateSharedListModalComponent,
      {
        size: "md",
        groupId: this.groupId,
        forEdit: true,
        initialValues: {
          title: this.list.title,
          description: this.list.description,
          stableUrl: ""
        }
      }
    ).subscribe((r : SharedListModalResult | null) => {
      const updates = {
        title: r.title,
        description: r.description,
        stableUrl: r.stableUrl
      };
      this.watchlist.editSharedList(
        this.list.objectId, updates
      ).subscribe((success : boolean) => {
        if (success) {
          this.list.title = updates.title;
          this.list.description = updates.description;
          // this.list.stableUrl = updates.stableUrl;
        }
      });
    });
  }

  public showEditDialog (entry : WatchlistEntry) : void
  {
    this.modals.stringPrompt({
      title: "Edit note",
      body: "Enter a new note:",
      placeholder: "Context for the worklist entry",
      acceptEmptyResult: true,
      initialValue: entry.note
    }).subscribe(noteString => {
      this.watchlist.editNote(
        entry,
        noteString,
        entry.categories,
        this.list.objectId
      ).subscribe(
        (success : boolean) => console.log(`Work list item note updated!`)
      );
    });
  }

  /**
   * Toggle whether the user gets email notifications for a watchlist item.
   * @param entry The entry to save
   */
  public changeNotificationSetting (entry : WatchlistEntry) : void
  {
    console.error("Notifications not yet supported for shared lists");
    return;
    // this.watchlist.changeNotificationSetting(entry, !entry.emailNotifyOnUpdate)
    //   .subscribe((success : boolean) =>
    //     entry.emailNotifyOnUpdate = (entry.emailNotifyOnUpdate !== success));
    //     // this is XORing the operands; when successful, invert the value
  }

  public open (entry : WatchlistEntry, target? : Algorithm) : void
  {
    this.watchlist.openShared(
      entry,
      this.list.objectId,
      !this.asSelector,
      target
    ).subscribe((subject : Subject) => this.selection.emit(subject));
  }

  public openDefault (entry : WatchlistEntry) : void
  {
    const target : Algorithm | null =
      this.algorithms.defaultForOpen(entry.contextType);
    this.open(entry, target);
  }

  public moveCopyPersonal (entry : WatchlistEntry, isMove : boolean = false)
  {
    return this.alerts.warn("You do not have permission to move or copy list entries");

    // TODO
    this.watchlist.watchSubject(
      this.subjects.fromReference(
        this.watchlist.createRef(entry.watchId, this.list.objectId))).subscribe((newWatchId?: number) => {
        if (isMove) {
          this.watchlist.unwatch(entry, true, false, this.list.objectId);
        } else {
          // alert!
        }
      })
  }

  public dismiss (entry : WatchlistEntry) : void
  {
    this.alerts.warn("You cannot dismiss alerts for shared worklist entries");
    return;
    // this.watchlist.clearAlert(entry).subscribe();
  }

  public static DRAG_ITEM_TYPE : string = "application/vnd.mcp-workitem";

  // public startDrag (event : DragEvent, id : number) : void
  // {
  //   debugger;
  //   return;

  //   event.dataTransfer.setData(WatchlistComponent.DRAG_ITEM_TYPE, `${id}`);
  //   event.dataTransfer.effectAllowed = "move";
  // }

  // public allowDrag (event : DragEvent, toFlag? : any) : boolean
  // {
  //   if (toFlag)
  //   {
  //     toFlag.highlight = true;
  //   }
  //   if (event.dataTransfer.types.indexOf(
  //     WatchlistComponent.DRAG_ITEM_TYPE) > -1)
  //   {
  //     event.dataTransfer.effectAllowed = "move";
  //     event.preventDefault();
  //     return false;
  //   }
  //   return true;
  // }

  // public dropItem (event : DragEvent, folder : string, table : WatchlistTable) 
  //   : void
  // {
  //   const id : number = Number.parseInt(
  //     event.dataTransfer.getData(WatchlistComponent.DRAG_ITEM_TYPE), 10);
  //     const entry : WatchlistEntry = this.watchlist.personalEntriesById.get(id);
  //   this.watchlist.edit(entry, entry.note, (folder ? [folder] : [])).subscribe(
  //     (ok : boolean) => {
  //       if (ok)
  //       {
  //         entry.categories = [folder];
  //         this.moveToFrontOfFolder(entry, table);
  //       }
  //     });
  // }

  // private moveToFrontOfFolder (entry : WatchlistEntry, table : WatchlistTable)
  //  : void
  // {
  //   const source : number = table.entries.findIndex(hardEquals(entry));
  //   if (source === -1)
  //   {
  //     return console.warn(
  //       `ID ${entry.watchId} not found in table ${table.contextType}`);
  //   }
  //   const target : number = table.entries.findIndex((e : WatchlistEntry) =>
  //     e.categories && e.categories[0] === entry.categories[0] && e !== entry);
  //   table.entries.splice(target, 0, ...table.entries.splice(source, 1));
  // }

  // public assignFolder (entry : WatchlistEntry) : void
  // {
  //   this.modals.stringPrompt({
  //     title: "Assign a folder",
  //     body: oneline`
  //       Choose a folder for
  //       ${Subject.readableTitleFor(entry.summary, entry.contextType)}.
  //       (By entering a new name, you will create a new folder.)`,
  //     placeholder: "Leave empty to unassign",
  //     acceptEmptyResult: true
  //   }).subscribe((label : string) => {
  //     this.watchlist.edit(entry, entry.note, (label ? [label] : [])).subscribe(
  //       (ok : boolean) => {
  //         if (ok)
  //         {
  //           entry.categories = [label];
  //         }
  //       });
  //   });
  // }

  // public firstOfFolder (entry : WatchlistEntry, table : WatchlistTable)
  //   : boolean
  // {
  //   const i : number = table.entries.findIndex(
  //     (x : WatchlistEntry) => x === entry);
  //   if (i === -1)
  //   {
  //     console.error("Cannot find entry in table:", entry);
  //     return false;
  //   }
  //   if (i === 0)
  //   {
  //     return true;
  //   }
  //   const prev : WatchlistEntry = table.entries[i - 1];
  //   return entry.categories[0] !== prev.categories[0];
  // }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/watchlist/shared-worklist.component.ts